import type { SupportedLocale } from './constants'

/** Default until /api/translate capabilities load (Workers AI locales). */
const DEFAULT_TRANSLATABLE = ['en', 'es', 'nl'] as const

let capabilitiesPromise: Promise<string[]> | null = null
let cachedLocales: string[] | null = null

export function normalizeAppLocale(
  locale: string | null | undefined,
): SupportedLocale {
  const base = (locale || 'en').toLowerCase().split('-')[0]
  if (base === 'papiamento' || base === 'pap') return 'pap'
  if (base === 'es' || base === 'nl' || base === 'en') return base
  return 'en'
}

/** Locales the server can translate between (includes pap when Google is configured). */
export async function getTranslatableLocales(): Promise<string[]> {
  if (cachedLocales) return cachedLocales
  if (!capabilitiesPromise) {
    capabilitiesPromise = fetch('/api/translate', {
      headers: { Accept: 'application/json' },
    })
      .then(async (response) => {
        if (!response.ok) return [...DEFAULT_TRANSLATABLE]
        const body: unknown = await response.json().catch(() => null)
        if (
          body &&
          typeof body === 'object' &&
          'locales' in body &&
          Array.isArray((body as { locales: unknown }).locales)
        ) {
          return (body as { locales: unknown[] }).locales.filter(
            (value): value is string => typeof value === 'string',
          )
        }
        return [...DEFAULT_TRANSLATABLE]
      })
      .catch(() => [...DEFAULT_TRANSLATABLE])
      .then((locales) => {
        cachedLocales = locales
        return locales
      })
  }
  return capabilitiesPromise
}

export function canOfferTranslation(
  sourceLocale: string | null | undefined,
  uiLocale: string | null | undefined,
  translatableLocales: readonly string[],
): boolean {
  const source = (sourceLocale || 'en').split('-')[0]
  const target = (uiLocale || 'en').split('-')[0]
  if (source === target) return false
  return (
    translatableLocales.includes(source) &&
    translatableLocales.includes(target)
  )
}

export async function translateTexts(input: {
  source_locale: string
  target_locale: string
  texts: Record<string, string>
}): Promise<{
  translations: Record<string, string>
  cached: boolean
}> {
  const response = await fetch('/api/translate', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  })

  const body: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const message =
      body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : `Translation failed (${response.status})`
    throw new Error(message)
  }

  if (!body || typeof body !== 'object' || !('translations' in body)) {
    throw new Error('Translation was not returned')
  }

  const payload = body as {
    translations?: Record<string, string>
    cached?: boolean
  }
  return {
    translations: payload.translations ?? {},
    cached: Boolean(payload.cached),
  }
}
